// Import necessary modules and types
import { db, firebaseImport } from '../../config/main_config';
import { nameFromUID } from './nameFromUID.util';
import firebase from 'firebase/app';
import 'firebase/firestore';
import { sanitise } from './sanitise.util';

// Define interfaces
export interface InterestedParty {
    dateAdded?: firebaseImport.firestore.Timestamp;
    dateUpdated?: firebaseImport.firestore.Timestamp;
    party?: string;
    requirement?: string;
    requirementsAddressedThroughISMS?: string;
    updatedBy?: string;
    frameworks?: string[];
    // Additional properties can be added as needed
}

export interface InterestedParties {
    [key: string]: InterestedParty;
}

interface ISMSManual {
    partiesInterested?: InterestedParties;
    [key: string]: any;
    // Additional properties can be added as needed
}

/**
 * Fetches the interested parties from Firestore based on the company ID.
 * Optionally sets up a real-time listener to automatically update and parse the data.
 * 
 * @param companyID - The company identifier used to locate the Firestore document.
 * @param options - Optional parameters to control behavior.
 * @param options.useSnapshot - Whether to use a real-time listener.
 * @param options.expanded - Whether the accordion content should be expanded.
 * @param options.onUpdate - Callback function to handle the parsed HTML.
 * @returns If useSnapshot is true, returns the unsubscribe function. Otherwise, returns the interested parties data or null.
 */
export async function getInterestedParties(
    companyID: string,
    options?: {
        useSnapshot?: boolean;
        expanded?: boolean;
        onUpdate?: (html: string) => void;
    }
): Promise<InterestedParties | null | (() => void)> {
    const docRef = db.doc(
        `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`
    );

    if (options?.useSnapshot) {
        // Set up real-time listener
        const unsubscribe = docRef.onSnapshot(async (doc) => {
            if (doc.exists) {
                const data = doc.data() as ISMSManual;
                const partiesInterested = data.partiesInterested || {};

                // Parse the data into HTML
                const html = await parseInterestedParties(partiesInterested, options.expanded);

                // Call the onUpdate callback with the new HTML
                if (options.onUpdate) {
                    options.onUpdate(html);
                }
            } else {
                console.log('No such document!');
                if (options.onUpdate) {
                    options.onUpdate('');
                }
            }
        }, (error) => {
            console.error('Error getting document:', error);
            if (options?.onUpdate) {
                options.onUpdate('');
            }
        });

        return unsubscribe;
    } else {
        // Fetch data once
        try {
            const doc = await docRef.get();
            if (doc.exists) {
                const data = doc.data() as ISMSManual;
                return data.partiesInterested || {};
            } else {
                console.log('No such document!');
                return null;
            }
        } catch (error) {
            console.error('Error getting document:', error);
            return null;
        }
    }
}

/**
 * Renders an array of objects or primitive values into a string of HTML tags.
 * If an item is an object, the function displays the value of a specified property or the entire object values.
 * For `standard` items, it displays them as badges instead of links.
 * If `tag` is provided, the function appends `${tag}-${content}` to the displayed text.
 *
 * @param {Array<any>} arr - An array that contains objects or primitive values (strings, numbers).
 * @param {string | null} [property=null] - The property to extract from an object in the array, or null to display all properties.
 * @param {string | null} [tag=null] - An optional tag to prepend to the displayed content in the anchor tags or badges.
 * @returns {string} A string of HTML tags, where each item in the array is rendered either as an anchor tag or badge.
 */
function renderArray(
    arr: Array<any>, // Array containing objects or primitive values
    property: string | null = null, // Property name to extract from objects (if applicable)
    tag: string | null = null // Optional tag to prepend to content
): string {
    return arr
        .map((item) => {
            // Check if the item is an object and not an array
            if (typeof item === 'object' && !Array.isArray(item)) {
                // Get the content to display based on the provided property or all object values
                const content: string = property && item[property] ? item[property] : Object.values(item).join(', ');

                // If the property is 'standard', display it as a badge
                if (property === 'standard') {
                    return `<span class="badge">${tag ? `${tag}-${content}` : content}</span>`;
                }

                // Return the anchor tag with optional tag prepended
                return `<a class="hover-info" href="#">${tag ? `${tag}-${content}` : content}</a>`;
            }

            // Handle primitive values like strings or numbers
            const itemContent: string = String(item); // Convert the value to a string

            // If the property is 'standard', display it as a badge for primitive values
            if (property === 'standard') {
                return `<span class="badge">${tag && !itemContent.includes(tag) ? `${tag}-${itemContent}` : itemContent}</span>`;
            }

            // Return the anchor tag with optional tag prepended, ensuring the tag is not already in the content
            return `<a class="hover-info" href="#">${tag && !itemContent.includes(tag) ? `${tag}-${itemContent}` : itemContent}</a>`;
        })
        .join(property === 'standard' ? ' ' : ', '); // Join all anchor tags or badges based on the property type
}
/**
 * Parses the interested parties data into HTML table rows, sorted by their IDs.
 * 
 * @param data - The interested parties data to parse.
 * @param expanded - A boolean indicating whether the accordion content should be expanded.
 * @returns A promise that resolves to an HTML string representing the sorted table rows.
 */
export async function parseInterestedParties(
    data: InterestedParties,
    expanded: boolean = false
): Promise<string> {
    let html = '';

    // Extract and sort the keys (IDs) of the interested parties
    const sortedKeys = Object.keys(data).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));

    for (const key of sortedKeys) {
        const item = data[key];
        console.log(item);

        const dateAdded =
            item.dateAdded instanceof firebaseImport.firestore.Timestamp
                ? item.dateAdded.toDate().toLocaleDateString('en-AU')
                : 'Please Update This Party';

        const dateUpdated =
            item.dateUpdated instanceof firebaseImport.firestore.Timestamp
                ? item.dateUpdated.toDate().toLocaleDateString('en-AU')
                : 'Please Update This Party';

        const party = item.party || 'Please Update This Party';
        const requirement = item.requirement || 'Please Update This Party';
        const requirementsAddressedThroughISMS = item.requirementsAddressedThroughISMS || '';

        const updatedBy = item.updatedBy
            ? await nameFromUID(item.updatedBy)
            : 'Unknown';

        const frameworks = item.frameworks
            ? renderArray(item.frameworks, 'standard')
            : '';

        html += `
            <tr class="tableordion-row" data-item-id="${key}">
                <td>${party}</td>
                <td>${sanitise(requirement)}</td>
                <td>${sanitise(requirementsAddressedThroughISMS)}</td>
                <td><div class="badges">${frameworks}</div></td>
                <td class="action-buttons">
                    <button class="action-btn" onclick="openEditInterestedPartyModal('${key}')" data-party-id="${key}">
                        <i class="fa-duotone fa-solid fa-pencil edit-icon" alt="Edit"></i>
                    </button>
                    <button class="action-btn" onclick="deleteInterestedPartyHandler(event, '${key}')">
                        <i class="fa-duotone color-red fa-trash delete-icon" alt="Delete"></i>
                    </button>
                    <button class="action-btn collapse-toggle" data-toggle="${expanded ? 'collapse' : 'expand'}">
                        <i class="fa-duotone fa-square-chevron-${expanded ? 'up' : 'down'}" style="color: ${expanded ? '#F6961D' : '#585555'};"></i>
                    </button>
                </td>
            </tr>
            <tr class="tableordion-content ${expanded ? 'accordion-content-active' : 'hidden'}" data-item-id="${key}" style="display: ${expanded ? 'table-row' : 'none'};">
                <td colspan="5">
                    <div class="accordion-inner-content">
                        <table>
                            <thead>
                                <tr class="tableordion-header">
                                    <th>Date Added</th>
                                    <th>Date Updated</th>
                                    <th>Updated By</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>${dateAdded}</td>
                                    <td>${dateUpdated}</td>
                                    <td>${updatedBy}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </td>
            </tr>`;
    }

    return html;
}